/**
 * Safety Culture Survey Module
 * Anonymous safety culture assessments with analytics dashboard
 * Aligned with ICAO Doc 9859 SMS Manual
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Search, BarChart3, Users, CheckCircle, Clock,
  Play, Pause, Lock, Eye, ChevronRight, ChevronLeft, Send, Target,
  TrendingUp, TrendingDown, Minus, AlertTriangle, Award, Building,
  UserCircle, FileText, Settings, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configuration
const STATUS_CONFIG = {
  draft: { color: 'bg-gray-500/20 text-gray-400', label: 'Draft', icon: FileText },
  active: { color: 'bg-emerald-500/20 text-emerald-400', label: 'Active', icon: Play },
  paused: { color: 'bg-amber-500/20 text-amber-400', label: 'Paused', icon: Pause },
  closed: { color: 'bg-red-500/20 text-red-400', label: 'Closed', icon: Lock },
  archived: { color: 'bg-slate-500/20 text-slate-400', label: 'Archived', icon: Lock }
};

// Category colors for analytics
const CATEGORY_COLORS = {
  reporting_culture: 'bg-blue-500',
  just_culture: 'bg-purple-500',
  learning_culture: 'bg-emerald-500',
  informed_culture: 'bg-cyan-500',
  leadership: 'bg-amber-500',
  communication: 'bg-orange-500',
  training_competence: 'bg-pink-500',
  workload_resources: 'bg-red-500',
  flexible_culture: 'bg-indigo-500',
  overall: 'bg-slate-500'
};

// Likert scale labels
const LIKERT_LABELS = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'];

export default function SafetyCultureSurveyPage() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [showRespondDialog, setShowRespondDialog] = useState(false);
  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);
  
  // Form state
  const [newSurvey, setNewSurvey] = useState({
    title: '',
    title_ar: '',
    description: '',
    description_ar: '',
    is_anonymous: true
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [surveysRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/safety-culture/surveys`, { headers }),
        fetch(`${API_URL}/api/safety-culture/stats/summary`, { headers })
      ]);
      
      if (surveysRes.ok) setSurveys(await surveysRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateSurvey = async () => {
    if (!newSurvey.title) {
      toast.error('Please enter a survey title');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/safety-culture/surveys?use_default_questions=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newSurvey)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Survey created with default questions');
        setShowCreateDialog(false);
        setNewSurvey({ title: '', title_ar: '', description: '', description_ar: '', is_anonymous: true });
        fetchData();
        // Open the new survey
        setSelectedSurvey(data);
        setShowSurveyDialog(true);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create survey');
      }
    } catch (error) {
      toast.error('Error creating survey');
    }
  };

  const handleActivateSurvey = async (surveyId) => {
    try {
      const res = await fetch(`${API_URL}/api/safety-culture/surveys/${surveyId}/activate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Survey activated');
        fetchData();
        setShowSurveyDialog(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to activate survey');
      }
    } catch (error) {
      toast.error('Error activating survey');
    }
  };

  const handleCloseSurvey = async (surveyId) => {
    try {
      const res = await fetch(`${API_URL}/api/safety-culture/surveys/${surveyId}/close`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Survey closed');
        fetchData();
      } else {
        toast.error('Failed to close survey');
      }
    } catch (error) {
      toast.error('Error closing survey');
    }
  };

  const handleViewAnalytics = async (survey) => {
    try {
      const res = await fetch(`${API_URL}/api/safety-culture/surveys/${survey.id}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
        setSelectedSurvey(survey);
        setShowAnalyticsDialog(true);
      } else {
        toast.error('Failed to load analytics');
      }
    } catch (error) {
      toast.error('Error loading analytics');
    }
  };

  const filteredSurveys = surveys.filter(s =>
    s.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.survey_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeSurveys = surveys.filter(s => s.status === 'active');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Loading safety culture surveys...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('safetyCulture.title')}</h1>
          <p className="text-slate-400 mt-1">{t('safetyCulture.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            {t('safetyCulture.newSurvey')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/20">
                  <ClipboardList className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_surveys}</p>
                  <p className="text-xs text-slate-400">{t('safetyCulture.surveys')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <Play className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.active_surveys}</p>
                  <p className="text-xs text-slate-400">{t('common.active')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Users className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_responses}</p>
                  <p className="text-xs text-slate-400">Responses</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Award className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.average_score?.toFixed(1) || '-'}</p>
                  <p className="text-xs text-slate-400">Avg Score</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-sky-500/20">
                  <Target className="w-5 h-5 text-sky-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.industry_benchmark}</p>
                  <p className="text-xs text-slate-400">Benchmark</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="surveys">All Surveys</TabsTrigger>
          <TabsTrigger value="respond">Respond</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Active Surveys */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Play className="w-5 h-5 text-emerald-400" />
                  Active Surveys
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activeSurveys.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No active surveys</p>
                    <p className="text-sm mt-1">Create a survey and activate it to start collecting responses</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activeSurveys.map(survey => (
                      <div
                        key={survey.id}
                        className="p-4 rounded-lg bg-slate-900/50 border border-slate-700 hover:border-slate-600 cursor-pointer"
                        onClick={() => handleViewAnalytics(survey)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-white">{survey.title}</h4>
                            <p className="text-sm text-slate-400">{survey.survey_number}</p>
                          </div>
                          <Button variant="ghost" size="sm">
                            <BarChart3 className="w-4 h-4 mr-1" /> Analytics
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Results Preview */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-purple-400" />
                  Culture Dimensions
                </CardTitle>
                <CardDescription>Key safety culture areas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { name: 'Reporting Culture', score: 4.2, benchmark: 3.8 },
                    { name: 'Just Culture', score: 3.8, benchmark: 3.6 },
                    { name: 'Learning Culture', score: 4.0, benchmark: 3.9 },
                    { name: 'Leadership', score: 4.3, benchmark: 4.0 },
                    { name: 'Communication', score: 3.5, benchmark: 3.7 }
                  ].map((dim, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-slate-300">{dim.name}</span>
                          <span className="text-white font-medium">{dim.score.toFixed(1)}</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${dim.score >= dim.benchmark ? 'bg-emerald-500' : 'bg-amber-500'}`}
                            style={{ width: `${(dim.score / 5) * 100}%` }}
                          />
                        </div>
                      </div>
                      {dim.score >= dim.benchmark ? (
                        <TrendingUp className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-amber-400" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Surveys Tab */}
        <TabsContent value="surveys" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">All Surveys</CardTitle>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search surveys..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64 bg-slate-900/50 border-slate-600"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {filteredSurveys.map(survey => {
                    const statusConfig = STATUS_CONFIG[survey.status] || STATUS_CONFIG.draft;
                    const StatusIcon = statusConfig.icon;
                    
                    return (
                      <div
                        key={survey.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-slate-900/50 border border-slate-700 hover:border-slate-600"
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-purple-500/20">
                            <ClipboardList className="w-5 h-5 text-purple-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">{survey.survey_number}</span>
                              <Badge className={statusConfig.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {statusConfig.label}
                              </Badge>
                              {survey.is_anonymous && (
                                <Badge variant="outline" className="text-xs border-slate-600">Anonymous</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-400 mt-1">{survey.title}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {survey.questions?.length || 0} questions
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {survey.status === 'draft' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleActivateSurvey(survey.id)}
                              className="border-emerald-500/50 text-emerald-400"
                            >
                              <Play className="w-4 h-4 mr-1" /> Activate
                            </Button>
                          )}
                          {survey.status === 'active' && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleViewAnalytics(survey)}
                              >
                                <BarChart3 className="w-4 h-4 mr-1" /> Analytics
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleCloseSurvey(survey.id)}
                                className="border-red-500/50 text-red-400"
                              >
                                <Lock className="w-4 h-4 mr-1" /> Close
                              </Button>
                            </>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setSelectedSurvey(survey);
                              setShowSurveyDialog(true);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  
                  {filteredSurveys.length === 0 && (
                    <div className="text-center py-12 text-slate-400">
                      <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No surveys found</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Respond Tab */}
        <TabsContent value="respond" className="mt-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Available Surveys</CardTitle>
              <CardDescription>Participate in active safety culture assessments</CardDescription>
            </CardHeader>
            <CardContent>
              {activeSurveys.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No active surveys available</p>
                  <p className="text-sm mt-1">Check back later for new surveys</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {activeSurveys.map(survey => (
                    <Card 
                      key={survey.id} 
                      className="bg-slate-900/50 border-slate-700 hover:border-blue-500/50 cursor-pointer transition-all"
                      onClick={() => {
                        setSelectedSurvey(survey);
                        setShowRespondDialog(true);
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-white">{survey.title}</h4>
                            <p className="text-sm text-slate-400 mt-1">{survey.description || 'Safety culture assessment'}</p>
                            <div className="flex items-center gap-2 mt-3">
                              <Badge variant="outline" className="text-xs">
                                {survey.questions?.length || 0} questions
                              </Badge>
                              {survey.is_anonymous && (
                                <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                                  Anonymous
                                </Badge>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Survey Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Create Safety Culture Survey</DialogTitle>
            <DialogDescription>
              Create a new survey with pre-built ICAO-aligned questions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Survey Title *</Label>
              <Input
                value={newSurvey.title}
                onChange={(e) => setNewSurvey(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Q1 2025 Safety Culture Assessment"
                className="bg-slate-800 border-slate-600"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Arabic Title (optional)</Label>
              <Input
                value={newSurvey.title_ar}
                onChange={(e) => setNewSurvey(prev => ({ ...prev, title_ar: e.target.value }))}
                placeholder="العنوان بالعربية"
                className="bg-slate-800 border-slate-600"
                dir="rtl"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newSurvey.description}
                onChange={(e) => setNewSurvey(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the survey purpose..."
                className="bg-slate-800 border-slate-600"
              />
            </div>
            
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-300">26 pre-built questions will be added</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Questions cover all ICAO safety culture dimensions: Reporting, Just Culture, Learning, Leadership, etc.
              </p>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateSurvey} className="bg-blue-600 hover:bg-blue-700">
                Create Survey
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Survey Details Dialog */}
      <Dialog open={showSurveyDialog} onOpenChange={setShowSurveyDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedSurvey?.survey_number}
              {selectedSurvey && (
                <Badge className={STATUS_CONFIG[selectedSurvey.status]?.color}>
                  {STATUS_CONFIG[selectedSurvey.status]?.label}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>{selectedSurvey?.title}</DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[60vh] mt-4 pr-4">
            {selectedSurvey && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-slate-800/50">
                  <h4 className="font-medium text-white mb-2">Survey Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-400">Questions:</span>
                      <span className="text-white ml-2">{selectedSurvey.questions?.length || 0}</span>
                    </div>
                    <div>
                      <span className="text-slate-400">Anonymous:</span>
                      <span className="text-white ml-2">{selectedSurvey.is_anonymous ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-white mb-3">Questions Preview</h4>
                  <div className="space-y-2">
                    {(selectedSurvey.questions || []).slice(0, 10).map((q, idx) => (
                      <div key={q.id} className="p-3 rounded-lg bg-slate-800/50 text-sm">
                        <span className="text-slate-400">{idx + 1}.</span>
                        <span className="text-white ml-2">{q.question_text}</span>
                        <Badge variant="outline" className="ml-2 text-xs">
                          {q.category?.replace('_', ' ')}
                        </Badge>
                      </div>
                    ))}
                    {(selectedSurvey.questions?.length || 0) > 10 && (
                      <p className="text-center text-slate-400 text-sm">
                        ... and {selectedSurvey.questions.length - 10} more questions
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Survey Response Dialog */}
      <SurveyResponseDialog
        open={showRespondDialog}
        onOpenChange={setShowRespondDialog}
        survey={selectedSurvey}
        token={token}
        onComplete={() => {
          setShowRespondDialog(false);
          toast.success('Thank you for your response!');
          fetchData();
        }}
      />

      {/* Analytics Dialog */}
      <Dialog open={showAnalyticsDialog} onOpenChange={setShowAnalyticsDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              Survey Analytics - {analytics?.survey_number}
            </DialogTitle>
            <DialogDescription>{analytics?.title}</DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[70vh] mt-4 pr-4">
            {analytics && (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-white">{analytics.total_responses}</p>
                      <p className="text-xs text-slate-400">Responses</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-400">{analytics.overall_score?.toFixed(1)}</p>
                      <p className="text-xs text-slate-400">Overall Score</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <p className="text-3xl font-bold text-sky-400">{analytics.overall_benchmark}</p>
                      <p className="text-xs text-slate-400">Benchmark</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-4 text-center">
                      <p className={`text-3xl font-bold ${analytics.overall_score >= analytics.overall_benchmark ? 'text-emerald-400' : 'text-amber-400'}`}>
                        {analytics.overall_score >= analytics.overall_benchmark ? '+' : ''}{(analytics.overall_score - analytics.overall_benchmark).toFixed(1)}
                      </p>
                      <p className="text-xs text-slate-400">vs Benchmark</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Category Scores */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Category Scores</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(analytics.category_scores || []).map((cat, idx) => (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-300">{cat.category_name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{cat.average_score?.toFixed(1)}</span>
                              <span className={`text-xs ${cat.vs_benchmark >= 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                                ({cat.vs_benchmark >= 0 ? '+' : ''}{cat.vs_benchmark?.toFixed(1)})
                              </span>
                            </div>
                          </div>
                          <div className="h-3 bg-slate-700 rounded-full overflow-hidden relative">
                            <div 
                              className={`h-full rounded-full ${cat.average_score >= cat.benchmark ? 'bg-emerald-500' : 'bg-amber-500'}`}
                              style={{ width: `${(cat.average_score / 5) * 100}%` }}
                            />
                            {/* Benchmark marker */}
                            <div 
                              className="absolute top-0 h-full w-0.5 bg-white/50"
                              style={{ left: `${(cat.benchmark / 5) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Strengths & Improvements */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="bg-emerald-500/10 border-emerald-500/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-emerald-400 text-sm flex items-center gap-2">
                        <Award className="w-4 h-4" /> Strengths
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.strengths?.length > 0 ? (
                        <ul className="space-y-1 text-sm text-slate-300">
                          {analytics.strengths.map((s, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <CheckCircle className="w-3 h-3 text-emerald-400" /> {s}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-slate-400">No strengths identified yet</p>
                      )}
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-amber-500/10 border-amber-500/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-amber-400 text-sm flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" /> Areas for Improvement
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.improvements?.length > 0 ? (
                        <ul className="space-y-1 text-sm text-slate-300">
                          {analytics.improvements.map((s, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <Target className="w-3 h-3 text-amber-400" /> {s}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-slate-400">No improvement areas identified</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Survey Response Dialog Component
function SurveyResponseDialog({ open, onOpenChange, survey, token, onComplete }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [department, setDepartment] = useState('');
  const [roleCategory, setRoleCategory] = useState('');
  
  useEffect(() => {
    if (survey) {
      setCurrentQuestion(0);
      setResponses({});
    }
  }, [survey]);
  
  if (!survey) return null;
  
  const questions = survey.questions || [];
  const totalQuestions = questions.length;
  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;
  
  const handleAnswer = (value) => {
    setResponses(prev => ({
      ...prev,
      [question.id]: {
        question_id: question.id,
        question_number: question.question_number,
        value,
        category: question.category
      }
    }));
  };
  
  const handleNext = () => {
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };
  
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const responseArray = Object.values(responses);
      const res = await fetch(
        `${API_URL}/api/safety-culture/surveys/${survey.id}/responses?department=${department}&role_category=${roleCategory}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(responseArray)
        }
      );
      
      if (res.ok) {
        onComplete();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit response');
      }
    } catch (error) {
      toast.error('Error submitting response');
    } finally {
      setSubmitting(false);
    }
  };
  
  const isLastQuestion = currentQuestion === totalQuestions - 1;
  const currentAnswer = responses[question?.id]?.value;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-white">{survey.title}</DialogTitle>
          <DialogDescription>
            Question {currentQuestion + 1} of {totalQuestions}
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress Bar */}
        <div className="space-y-2">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-slate-400 text-right">{Math.round(progress)}% complete</p>
        </div>
        
        {/* Question */}
        {question && (
          <div className="py-6">
            <Badge variant="outline" className="mb-3 text-xs">
              {question.category?.replace('_', ' ')}
            </Badge>
            <h3 className="text-lg text-white mb-6">{question.question_text}</h3>
            
            {/* Likert Scale */}
            <RadioGroup
              value={String(currentAnswer || '')}
              onValueChange={(v) => handleAnswer(parseInt(v))}
              className="space-y-3"
            >
              {LIKERT_LABELS.map((label, idx) => (
                <div
                  key={idx}
                  className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                    currentAnswer === idx + 1
                      ? 'bg-blue-500/20 border-blue-500/50'
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }`}
                  onClick={() => handleAnswer(idx + 1)}
                >
                  <RadioGroupItem value={String(idx + 1)} id={`option-${idx}`} />
                  <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer">
                    <span className="text-white">{label}</span>
                    <span className="text-slate-400 ml-2">({idx + 1})</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        )}
        
        {/* Demographics (last question) */}
        {isLastQuestion && currentAnswer && (
          <div className="space-y-4 p-4 rounded-lg bg-slate-800/50">
            <p className="text-sm text-slate-400">Optional demographic information (anonymous)</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs">Department</Label>
                <Select value={department} onValueChange={setDepartment}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="flight_ops">Flight Operations</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="ground_ops">Ground Operations</SelectItem>
                    <SelectItem value="cabin_crew">Cabin Crew</SelectItem>
                    <SelectItem value="safety">Safety</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Role Category</Label>
                <Select value={roleCategory} onValueChange={setRoleCategory}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="management">Management</SelectItem>
                    <SelectItem value="operational">Operational Staff</SelectItem>
                    <SelectItem value="support">Support Staff</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
        
        {/* Navigation */}
        <div className="flex items-center justify-between mt-4">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          
          {isLastQuestion ? (
            <Button
              onClick={handleSubmit}
              disabled={!currentAnswer || submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Send className="w-4 h-4 mr-1" /> {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!currentAnswer}
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
